import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Hourglass, Lock, Search } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, downloadFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatLastSeen } from "@/lib/presence";
import { useToast } from "@/lib/toast";
import { Button, Card, Input } from "@/components/app/Primitives";
import { StackPage } from "@/components/app/Layout";
import { CenterSpinner } from "@/components/app/Spinner";

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [stepUpDone, setStepUpDone] = useState(false);
  const [password, setPassword] = useState("");
  const [stepUpBusy, setStepUpBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);
  const [confirming, setConfirming] = useState(null);

  useEffect(() => { if (user && !user.platform_admin) navigate("/", { replace: true }); }, [user, navigate]);

  const load = useCallback(async (query = q) => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([api("/admin/stats"), api("/admin/users", { query: { q: query, page: "1", page_size: "50", include_deactivated: true } })]);
      setStats(s); setRows(list.rows); setTotal(list.total);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load";
      if (msg.startsWith("step_up_required")) { setStepUpDone(false); toast.show("Session expired — re-enter your password", "info"); } else toast.show(msg, "error");
    } finally { setLoading(false); }
  }, [q, toast]);

  const doStepUp = async () => {
    if (!password) return toast.show("Enter your password", "error");
    setStepUpBusy(true);
    try { await api("/admin/step-up", { method: "POST", body: { password } }); setStepUpDone(true); setPassword(""); await load(); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Wrong password", "error"); } finally { setStepUpBusy(false); }
  };
  const runAction = async () => {
    if (!confirming) return;
    const { row, mode } = confirming;
    try {
      if (mode === "deactivate") { await api(`/admin/users/${row.id}/deactivate`, { method: "POST" }); toast.show(`${row.full_name} deactivated`, "success"); }
      else if (mode === "reactivate") { await api(`/admin/users/${row.id}/reactivate`, { method: "POST" }); toast.show(`${row.full_name} reactivated`, "success"); }
      else { await api(`/admin/users/${row.id}`, { method: "DELETE" }); toast.show(`${row.full_name} deleted`, "info"); }
      setConfirming(null); await load();
    } catch (e) { toast.show(e instanceof Error ? e.message : "Action failed", "error"); }
  };
  const exportUsers = async () => {
    setExporting(true);
    try { await downloadFile("/admin/users.xlsx", `assignoo-users-${new Date().toISOString().slice(0, 10)}.xlsx`); toast.show("Excel downloaded", "success"); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Export failed", "error"); } finally { setExporting(false); }
  };

  if (!user?.platform_admin) return null;
  if (!stepUpDone) {
    return (
      <StackPage title="Admin" testID="admin-page">
        <Card>
          <div className="mb-2 flex items-center gap-2.5"><Lock size={20} className="text-a-primary" /><p className="text-base font-extrabold text-a-text">Re-enter your password</p></div>
          <p className="mb-3 text-[13px] text-a-muted">For your protection, the Admin console requires you to confirm your password. This grant lasts 10 minutes on this device.</p>
          <Input testID="admin-stepup-password" placeholder="Password" type="password" value={password} onChangeText={setPassword} onSubmitEditing={doStepUp} autoFocus />
          <div className="mt-2.5"><Button testID="admin-stepup-submit" label="Unlock Admin" onPress={doStepUp} loading={stepUpBusy} /></div>
        </Card>
      </StackPage>
    );
  }

  const chip = (ok, label) => <span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${ok ? "bg-a-success/15 text-a-success" : "bg-a-border text-a-muted"}`}>{label}</span>;
  return (
    <StackPage title="Admin Console" testID="admin-page" right={<button onClick={exportUsers} disabled={exporting} className="text-a-primary" data-testid="admin-export">{exporting ? <Hourglass size={22} /> : <Download size={22} />}</button>}>
      {stats ? (
        <div className="flex flex-wrap gap-2">
          {[["Total users", stats.total_users], ["Verified", stats.email_verified], ["Active today", stats.active_today], ["New this week", stats.new_this_week], ["Workspaces", stats.workspaces_total], ["Tasks", stats.tasks_total], ["Deactivated", stats.deactivated]].map(([l, v]) => (
            <div key={l} className="min-w-[100px] rounded-xl bg-a-dim px-3 py-2"><p className="text-xl font-extrabold text-a-primary">{v}</p><p className="text-[11px] text-a-text">{l}</p></div>
          ))}
        </div>
      ) : null}
      <div className="flex gap-2">
        <Input testID="admin-search" leading={<Search size={16} />} placeholder="Search name, email, mobile…" value={q} onChangeText={setQ} onSubmitEditing={() => load(q)} />
        <Button testID="admin-search-submit" label="Search" onPress={() => load(q)} fullWidth={false} className="h-12" />
      </div>
      <p className="text-xs text-a-muted">{total} total users</p>
      {loading && rows.length === 0 ? <CenterSpinner /> : rows.length === 0 ? <p className="mt-5 text-center text-a-muted">No users found.</p> : rows.map((r) => (
        <Card key={r.id} testID={`admin-user-${r.id}`}>
          <p className="text-[15px] font-extrabold text-a-text">{r.full_name || "(no name)"}</p>
          <p className="text-xs text-a-muted">{r.email}</p>
          {r.mobile_number ? <p className="text-xs text-a-muted">{r.mobile_number}</p> : null}
          {r.designation || r.company_name ? <p className="text-xs text-a-muted">{[r.designation, r.company_name].filter(Boolean).join(" · ")}</p> : null}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {chip(r.email_verified, r.email_verified ? "Email ✓" : "Email ✗")}
            {chip(r.mobile_verified, r.mobile_verified ? "Mobile ✓" : "Mobile ✗")}
            <span className="rounded-md bg-a-dim px-2 py-0.5 text-[11px] font-bold text-a-primary">{r.workspace_count} ws</span>
            {r.deactivated_at ? <span className="rounded-md bg-a-danger/15 px-2 py-0.5 text-[11px] font-bold text-a-danger">Deactivated</span> : null}
          </div>
          <p className="mt-1.5 text-[11px] text-a-muted">Joined {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"} · {r.presence_state === "active" ? "Active now" : r.last_active_at ? `Last seen ${formatLastSeen(r.last_active_at)}` : "Never active"}</p>
          <div className="mt-3 flex gap-2">
            {r.deactivated_at ? <Button label="Reactivate" size="sm" variant="secondary" fullWidth={false} onPress={() => setConfirming({ row: r, mode: "reactivate" })} />
              : <Button label="Deactivate" size="sm" variant="ghost" fullWidth={false} onPress={() => setConfirming({ row: r, mode: "deactivate" })} />}
            <Button label="Delete" size="sm" variant="danger" fullWidth={false} onPress={() => setConfirming({ row: r, mode: "delete" })} />
          </div>
        </Card>
      ))}
      <AlertDialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
        <AlertDialogContent className="border-a-border bg-a-surface text-a-text">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirming?.mode === "delete" ? "Delete user permanently?" : confirming?.mode === "deactivate" ? "Deactivate user?" : "Reactivate user?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-a-muted">{confirming?.row.full_name} ({confirming?.row.email}){confirming?.mode === "delete" ? " — this cannot be undone." : ""}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-a-border bg-transparent text-a-text" data-testid="admin-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runAction} className={confirming?.mode === "reactivate" ? "bg-a-primary text-white" : "bg-a-danger text-white"} data-testid="admin-confirm-ok">
              {confirming?.mode === "delete" ? "Delete forever" : confirming?.mode === "deactivate" ? "Deactivate" : "Reactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </StackPage>
  );
}
